/**
 * DELAY mode: ghost cursor follows input instantly (no collision, no cosmetics);
 * real cursor replays that path with a fixed lag and is the only vulnerable body.
 */

export const DELAY_LAG_SEC = 1;
export const DELAY_GHOST_ALPHA = 0.32;

type DelaySample = { t: number; x: number; y: number };

let gameTimeSec = 0;
let samples: DelaySample[] = [];
let startX = 0;
let startY = 0;
let realPrevX = 0;
let realPrevY = 0;

const MAX_SAMPLES = 600; // ~10 s at 60 Hz

export function resetDelayState(x: number, y: number): void {
  gameTimeSec = 0;
  startX = x;
  startY = y;
  realPrevX = x;
  realPrevY = y;
  samples = [{ t: 0, x, y }];
}

export function remapDelayHistory(prevW: number, prevH: number, nextW: number, nextH: number): void {
  if (prevW <= 0 || prevH <= 0 || nextW <= 0 || nextH <= 0) return;
  if (prevW === nextW && prevH === nextH) return;
  const sx = nextW / prevW;
  const sy = nextH / prevH;
  startX *= sx;
  startY *= sy;
  realPrevX *= sx;
  realPrevY *= sy;
  for (const s of samples) {
    s.x *= sx;
    s.y *= sy;
  }
}

/** Advance game clock and record the ghost (input) position. Call once per active gameplay frame. */
export function tickDelayGhost(dt: number, ghostX: number, ghostY: number): void {
  const d = Math.max(0, dt);
  gameTimeSec += d;
  const last = samples[samples.length - 1];
  if (!last || last.t < gameTimeSec - 1e-6) {
    samples.push({ t: gameTimeSec, x: ghostX, y: ghostY });
  } else {
    last.x = ghostX;
    last.y = ghostY;
  }
  const cutoff = gameTimeSec - DELAY_LAG_SEC - 1;
  while (samples.length > 2 && samples[1]!.t < cutoff) {
    samples.shift();
  }
  if (samples.length > MAX_SAMPLES) {
    samples.splice(0, samples.length - MAX_SAMPLES);
  }
}

export function getDelayGhostPos(ghostX: number, ghostY: number): { x: number; y: number } {
  return { x: ghostX, y: ghostY };
}

export function getDelayRealCursorPos(): { x: number; y: number } {
  const targetT = gameTimeSec - DELAY_LAG_SEC;
  if (targetT <= 0 || samples.length === 0) {
    return { x: startX, y: startY };
  }
  if (samples.length === 1) {
    return { x: samples[0]!.x, y: samples[0]!.y };
  }
  if (targetT >= samples[samples.length - 1]!.t) {
    const last = samples[samples.length - 1]!;
    return { x: last.x, y: last.y };
  }
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.t <= targetT) lo = mid;
    else hi = mid;
  }
  const a = samples[lo]!;
  const b = samples[hi]!;
  const span = b.t - a.t;
  const u = span > 1e-9 ? (targetT - a.t) / span : 0;
  return {
    x: a.x + (b.x - a.x) * u,
    y: a.y + (b.y - a.y) * u,
  };
}

export function getDelayRealPrev(): { x: number; y: number } {
  return { x: realPrevX, y: realPrevY };
}

export function syncDelayRealPrev(x: number, y: number): void {
  realPrevX = x;
  realPrevY = y;
}

export function drawDelayGhostCursorOnto(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  fillColor: string,
): void {
  c.save();
  c.globalCompositeOperation = "source-over";
  c.globalAlpha = DELAY_GHOST_ALPHA;
  c.fillStyle = fillColor;
  c.beginPath();
  c.arc(x, y, radius, 0, Math.PI * 2);
  c.fill();
  c.globalAlpha = Math.min(1, DELAY_GHOST_ALPHA + 0.18);
  c.strokeStyle = fillColor;
  c.lineWidth = Math.max(1, radius * 0.22);
  c.beginPath();
  c.arc(x, y, radius, 0, Math.PI * 2);
  c.stroke();
  c.restore();
}
